// PDP Reviews Toggle — collapsible callout that hides an adjacent yotpo-reviews
// Shopify section until the reader clicks. Reads rating and count from Yotpo's
// hydrated star widget (or from the server-rendered SEO block as a fallback).
//
// The section liquid (sections/pdp-reviews-toggle.liquid) renders the callout
// markup and passes copy strings via data attributes; this class handles all
// behavior. Modelled on the <sibling-group-swatches> pattern in this theme.

class PdpReviewsToggle extends HTMLElement {
  static SELECTORS = {
    reviewsSection: '.shopify-section[id*="yotpo-reviews"]',
    yotpoStarButton: ".yotpo-sr-bottom-line-summary",
    yotpoStarButtonHydrated: '.yotpo-sr-bottom-line-summary[aria-label]',
    yotpoSeoBlock: "#yotpo-reviews-overall-rating",
  };

  // Anchored — number must be adjacent to Yotpo's specific phrasing so a
  // review count can't leak into the rating slot (or vice versa).
  static PATTERNS = {
    starWidgetRating: /(\d+(?:\.\d+)?)\s*out\s*(?:of\s*)?5\s*star/i,
    starWidgetCount: /total\s+(\d+)\s+review/i,
    seoBlockRating: /(\d+(?:\.\d+)?)\s*\/\s*5/,
    seoBlockCount: /from\s+(\d+)\s+review/i,
  };

  static REVIEWS_HIDDEN_CLASS = "pdp-reviews-hidden";
  static HYDRATE_TIMEOUT_MS = 30000;

  #abort = null;
  #button = null;
  #reviewsSection = null;
  #done = false;
  #closedLabel = "";

  connectedCallback() {
    if (this.#abort) return;

    this.#reviewsSection = document.querySelector(
      PdpReviewsToggle.SELECTORS.reviewsSection
    );
    if (!this.#reviewsSection) return;

    this.#button = this.querySelector("[data-toggle-button]");
    if (!this.#button) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#closedLabel = this.dataset.readLabel || "";
    this.#reviewsSection.classList.add(PdpReviewsToggle.REVIEWS_HIDDEN_CLASS);
    this.#button.setAttribute("aria-controls", this.#reviewsSection.id);

    this.#button.addEventListener("click", this.#onToggleClick, { signal });
    document.addEventListener("click", this.#onStarWidgetClick, {
      signal,
      capture: true,
    });

    if (!this.#hydrate()) {
      this.#observeForData();
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  toggle() {
    if (this.#isOpen) this.close();
    else this.open();
  }

  open() {
    this.#button.setAttribute("aria-expanded", "true");
    this.#reviewsSection.classList.remove(PdpReviewsToggle.REVIEWS_HIDDEN_CLASS);
    this.#setCtaLabel(this.dataset.hideLabel);
    this.#scrollIntoView();
  }

  close() {
    this.#button.setAttribute("aria-expanded", "false");
    this.#reviewsSection.classList.add(PdpReviewsToggle.REVIEWS_HIDDEN_CLASS);
    this.#setCtaLabel(this.#closedLabel);
  }

  get #isOpen() {
    return this.#button.getAttribute("aria-expanded") === "true";
  }

  #onToggleClick = () => this.toggle();

  #onStarWidgetClick = (e) => {
    const btn = e.target.closest(PdpReviewsToggle.SELECTORS.yotpoStarButton);
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (this.#isOpen) this.#scrollIntoView();
    else this.open();
  };

  #setCtaLabel(text) {
    const el = this.querySelector("[data-cta-label]");
    if (el && text) el.textContent = text;
  }

  #scrollIntoView() {
    this.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  #hydrate() {
    if (this.#done) return true;
    const data = this.#extractFromStarWidget() || this.#extractFromSeoBlock();
    if (!data) return false;
    this.#applyData(data);
    return this.#done;
  }

  #extractFromStarWidget() {
    const el = document.querySelector(
      PdpReviewsToggle.SELECTORS.yotpoStarButtonHydrated
    );
    if (!el) return null;
    return this.#parseSignal(
      el.getAttribute("aria-label") || "",
      PdpReviewsToggle.PATTERNS.starWidgetRating,
      PdpReviewsToggle.PATTERNS.starWidgetCount
    );
  }

  #extractFromSeoBlock() {
    const el = document.querySelector(PdpReviewsToggle.SELECTORS.yotpoSeoBlock);
    if (!el) return null;
    return this.#parseSignal(
      (el.textContent || "").trim(),
      PdpReviewsToggle.PATTERNS.seoBlockRating,
      PdpReviewsToggle.PATTERNS.seoBlockCount
    );
  }

  #parseSignal(text, ratingPattern, countPattern) {
    if (!text) return null;
    const rm = text.match(ratingPattern);
    const cm = text.match(countPattern);
    if (!rm && !cm) return null;
    return {
      rating: rm ? parseFloat(rm[1]) : null,
      count: cm ? cm[1] : null,
    };
  }

  #applyData({ rating, count }) {
    if (count === "0") {
      this.#enterEmptyState();
      return;
    }
    const hasRating = rating != null && !isNaN(rating) && rating > 0;
    if (hasRating) this.#applyRating(rating);
    if (count) this.#applyCount(count);
    if (hasRating || count) {
      this.#hideFallbackHeadline();
      this.#done = true;
    }
  }

  #applyRating(rating) {
    const valueEl = this.querySelector("[data-rating-value]");
    const ratingEl = this.querySelector(".pdp-reviews-toggle__rating");
    const starsEl = this.querySelector(".pdp-reviews-toggle__stars");
    const fillEl = this.querySelector("[data-stars-fill]");
    if (valueEl) valueEl.textContent = rating.toFixed(1);
    if (ratingEl) ratingEl.hidden = false;
    if (starsEl) starsEl.hidden = false;
    if (fillEl) {
      fillEl.style.width = (Math.max(0, Math.min(5, rating)) / 5) * 100 + "%";
    }
  }

  #applyCount(count) {
    const countEl = this.querySelector("[data-count]");
    const starsEl = this.querySelector(".pdp-reviews-toggle__stars");
    if (countEl) {
      const template =
        count === "1"
          ? this.dataset.countSingular
          : this.dataset.countPluralTemplate;
      countEl.textContent = (template || "").replace("{count}", count);
      countEl.hidden = false;
    }
    if (starsEl) starsEl.hidden = false;
  }

  #enterEmptyState() {
    const headline = this.querySelector("[data-fallback-headline]");
    if (headline && this.dataset.noReviewsHeadline) {
      headline.textContent = this.dataset.noReviewsHeadline;
      headline.hidden = false;
    }
    this.#closedLabel = this.dataset.firstReviewLabel || this.#closedLabel;
    if (!this.#isOpen) this.#setCtaLabel(this.#closedLabel);
    [
      ".pdp-reviews-toggle__stars",
      ".pdp-reviews-toggle__rating",
      "[data-count]",
    ].forEach((sel) => {
      const el = this.querySelector(sel);
      if (el) el.hidden = true;
    });
    this.#done = true;
  }

  #hideFallbackHeadline() {
    const el = this.querySelector("[data-fallback-headline]");
    if (el) el.hidden = true;
  }

  #observeForData() {
    const observer = new MutationObserver(() => {
      if (this.#hydrate()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = setTimeout(
      () => observer.disconnect(),
      PdpReviewsToggle.HYDRATE_TIMEOUT_MS
    );
    this.#abort.signal.addEventListener("abort", () => {
      observer.disconnect();
      clearTimeout(timer);
    });
  }
}

if (!customElements.get("pdp-reviews-toggle")) {
  customElements.define("pdp-reviews-toggle", PdpReviewsToggle);
}
